/**
 * Drive canvas compositors at a steady rate even when this tab is in the background.
 * requestAnimationFrame stops in hidden tabs (e.g. while presenting another tab during a
 * screen-share take), which would freeze the recorded canvas; dedicated-worker timers keep running.
 */

const WORKER_SOURCE = `
let timer = null;
self.onmessage = (e) => {
  if (timer) clearInterval(timer);
  timer = null;
  if (e.data && e.data.interval) {
    timer = setInterval(() => self.postMessage(0), e.data.interval);
  }
};
`;

function createTickWorker() {
  if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined') {
    return null;
  }
  try {
    const url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
    const worker = new Worker(url);
    URL.revokeObjectURL(url);
    return worker;
  } catch {
    return null;
  }
}

/**
 * @param {(now: number) => void} onFrame
 * @param {{ fps?: number }} [options]
 * @returns {() => void} stop
 */
export function startFrameLoop(onFrame, { fps = 30 } = {}) {
  const interval = Math.max(10, Math.round(1000 / Math.max(1, fps)));
  const hasDocument = typeof document !== 'undefined';
  let stopped = false;
  let rafId = 0;
  let timeoutId = 0;
  let worker = null;

  const tick = () => {
    if (stopped) return;
    try {
      onFrame(typeof performance !== 'undefined' ? performance.now() : Date.now());
    } catch (err) {
      console.warn('Frame loop callback failed:', err);
    }
  };

  const stopBackground = () => {
    if (worker) worker.postMessage({ interval: 0 });
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = 0;
  };

  const runForeground = () => {
    if (stopped) return;
    tick();
    rafId = requestAnimationFrame(runForeground);
  };

  const runBackground = () => {
    if (!worker) worker = createTickWorker();
    if (worker) {
      worker.onmessage = tick;
      worker.postMessage({ interval });
      return;
    }
    const loop = () => {
      if (stopped) return;
      tick();
      timeoutId = setTimeout(loop, interval);
    };
    loop();
  };

  const sync = () => {
    if (stopped) return;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
    stopBackground();
    if (hasDocument && document.hidden) runBackground();
    else runForeground();
  };

  if (hasDocument) document.addEventListener('visibilitychange', sync);
  sync();

  return () => {
    stopped = true;
    if (hasDocument) document.removeEventListener('visibilitychange', sync);
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
    stopBackground();
    worker?.terminate();
    worker = null;
  };
}
